import React from 'react';
import { Clock, ShieldCheck, DollarSign, Award, Wrench, Sparkles, Phone, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
function CheckCircleIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-583">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-584"></path>
      <polyline points="22 4 12 14.01 9 11.01" data-id="element-585"></polyline>
    </svg>;
}
export function WhyChoose() {
  const reasons = [{
    icon: Clock,
    title: "You Won't Be Left Waiting",
    desc: 'We arrive when we say we will. Reliable, punctual service — because your time matters as much as ours.',
    stat: 'Reliable & punctual',
    num: '01'
  }, {
    icon: DollarSign,
    title: 'No Bill Shock. Ever.',
    desc: "You'll know the full cost before we start. No hidden fees, no surprise extras. The price we quote is the price you pay.",
    stat: 'Upfront fixed pricing',
    num: '02'
  }, {
    icon: Award,
    title: 'Fully Licensed & Insured',
    desc: 'Fully licensed, fully insured, and committed to meeting all Australian electrical standards on every job.',
    stat: 'Fully licensed',
    num: '03'
  }, {
    icon: UserCheck,
    title: 'Quality Over Shortcuts',
    desc: 'We attract customers who value quality workmanship over the cheapest price. Every job is done right the first time.',
    stat: '437+ five-star reviews',
    num: '04'
  }, {
    icon: Wrench,
    title: 'The Completely Electrical Guarantee',
    desc: "Every job is backed by our workmanship guarantee. If you're not 100% satisfied, we'll make it right.",
    stat: 'Guaranteed satisfaction',
    num: '05'
  }, {
    icon: Sparkles,
    title: 'Your Home Treated With Respect',
    desc: "We work tidily, protect your property, and leave your home spotless. You won't know we were there — except the problem is fixed.",
    stat: 'Clean & respectful',
    num: '06'
  }];
  return <section className="py-20 lg:py-28 bg-white relative overflow-hidden" data-id="element-586">
      <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-587">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-588">
          <span className="text-orange font-bold tracking-wider uppercase text-sm mb-3 block" data-id="element-589">
            The Completely Electrical Difference
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-navy mb-6 tracking-tighter" data-id="element-590">
            What You Can Expect When You Choose Us
          </h2>
          <p className="text-lg text-slate leading-relaxed" data-id="element-591">
            Had a bad experience with another electrician? You're not alone. We
            built Completely Electrical to be the electrician we'd want to hire
            ourselves — punctual, honest, and genuinely good at the job.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-14 items-center mb-14" data-id="element-592">
          {/* Left: Reason Cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5" data-id="element-593">
            {reasons.map((reason, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} className="bg-white border border-gray-100 p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all relative overflow-hidden group" data-id="element-594">
                <div className="absolute -top-4 -right-2 text-7xl font-heading font-extrabold text-gray-50 opacity-50 group-hover:text-orange/5 transition-colors pointer-events-none select-none" data-id="element-595">
                  {reason.num}
                </div>

                <div className="w-11 h-11 bg-orange/10 rounded-full flex items-center justify-center mb-3 relative z-10 group-hover:scale-110 transition-transform" data-id="element-596">
                  <reason.icon className="w-5 h-5 text-orange" data-id="element-597" />
                </div>

                <h3 className="text-base font-heading font-extrabold text-navy mb-1.5 relative z-10" data-id="element-598">
                  {reason.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed mb-3 relative z-10" data-id="element-599">
                  {reason.desc}
                </p>

                <div className="inline-flex items-center gap-1.5 bg-cream px-2.5 py-1 rounded-lg text-xs font-bold text-navy relative z-10" data-id="element-600">
                  <CheckCircleIcon className="w-3 h-3 text-emerald-500" data-id="element-601" />
                  {reason.stat}
                </div>
              </motion.div>)}
          </div>

          {/* Right: Electrician Photo */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5 relative hidden lg:block" data-id="element-602">
            <div className="absolute -top-8 -right-8 w-full h-full bg-cream rounded-[2rem] -rotate-3 z-0" data-id="element-603"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-orange/10 rounded-full z-0" data-id="element-604"></div>

            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl" data-id="element-605">
              <img src="https://cdn.magicpatterns.com/uploads/tpf5u5abkHZG3UnQ6ypWxt/dr-electrician_(1).jpg" alt="Completely Electrical licensed electrician with professional equipment" className="w-full h-auto object-cover" data-id="element-606" />
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 z-20 border border-gray-100" data-id="element-607">
              <div className="flex items-center gap-3" data-id="element-608">
                <div className="w-12 h-12 bg-orange/10 rounded-full flex items-center justify-center" data-id="element-609">
                  <ShieldCheck className="w-6 h-6 text-orange" data-id="element-610" />
                </div>
                <div data-id="element-611">
                  <div className="text-sm font-extrabold text-navy" data-id="element-612">
                    Quality Guaranteed
                  </div>
                  <div className="text-xs text-slate" data-id="element-613">
                    Fully Licensed & Insured
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex justify-center" data-id="element-614">
          <a href="tel:0418381988" className="bg-navy hover:bg-navy-600 text-white px-10 py-5 rounded-xl font-extrabold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:-translate-y-1" data-id="element-615">
            <Phone className="w-6 h-6 text-orange" data-id="element-616" />
            Experience the Difference — Call 0418 381 988
          </a>
        </div>
      </div>
    </section>;
}